import { Navigate } from "react-router-dom";
import { FileText, ReceiptText, ScrollText, Wallet } from "lucide-react";
import { useAuth } from "@/lib/auth";

const bg = "https://images.pexels.com/photos/36181031/pexels-photo-36181031.jpeg";

export default function Login() {
  const { user, loading } = useAuth();
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-12 bg-background" data-testid="login-page">
      <div className="lg:col-span-7 relative hidden lg:block overflow-hidden border-r border-border">
        <img src={bg} alt="Warm minimalist desk" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1A1A1A]/55" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-[#F9F9F8]">
          <div className="font-display font-black text-2xl tracking-tight">DocFlow</div>
          <div className="max-w-xl">
            <p className="overline !text-[#F9F9F8]/70 mb-4">Business documents, done properly</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]">
              Invoices, quotations, receipts and vouchers — signed and shared in minutes.
            </h1>
          </div>
          <div className="grid grid-cols-4 border border-[#F9F9F8]/30 divide-x divide-[#F9F9F8]/30">
            {[
              [FileText, "Invoices"],
              [ScrollText, "Quotations"],
              [ReceiptText, "Receipts"],
              [Wallet, "Vouchers"],
            ].map(([Icon, l]) => (
              <div key={l} className="p-5 flex items-center gap-3 text-sm">
                <Icon className="w-5 h-5" strokeWidth={1.5} /> {l}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="lg:col-span-5 flex flex-col justify-center p-8 sm:p-16 rise">
        <div className="font-display font-black text-2xl tracking-tight lg:hidden mb-16">DocFlow</div>
        <p className="overline mb-3">Welcome</p>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mb-4">Sign in to your workspace</h2>
        <p className="text-muted-foreground mb-10 leading-relaxed">
          Use your Google account. Your company profile, documents and signatures stay synced across desktop, tablet and mobile.
        </p>
        <button
          onClick={signIn}
          data-testid="google-signin-button"
          className="btn-press inline-flex items-center justify-center gap-3 h-14 px-8 bg-foreground text-background font-semibold text-base hover:bg-primary"
        >
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.5 13.3l7.8 6C12.2 13.6 17.6 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v8.1h12.7c-.3 2.1-1.7 5.3-4.8 7.4l7.4 5.7c4.4-4.1 7.2-10.1 7.2-17.2z" />
            <path fill="#FBBC05" d="M10.3 28.7A14.6 14.6 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.5 10.7l7.8-6z" />
            <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2 1.4-4.7 2.4-8.5 2.4-6.4 0-11.8-4.1-13.7-9.9l-7.8 6C6.5 42.6 14.6 48 24 48z" />
          </svg>
          Continue with Google
        </button>
        <p className="text-xs text-muted-foreground mt-6">Install DocFlow from your browser menu to use it like a native app.</p>
      </div>
    </div>
  );
}
